<script lang="ts" setup>
import { Browser } from '@capacitor/browser'
import { useRoute } from 'vue-router'

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/recipe/[id]/[slug]', // -> accessible at /about-us (no prefix since it's the default locale)
    fr: '/recette/[id]/[slug]', // -> accessible at /fr/a-propos
  },
})
definePageMeta({
  name: 'RecipePage',
})

// Métadonnées de la page de recette individuelle
const route = useRoute()
const config = useRuntimeConfig()
const userStore = useUserStore()
const modalStore = useModalStore()
const localePath = useLocalePath()
const toast = useToast()
const overlay = useOverlayWithAd()
const baseStore = useBaseStore()
const { maybeRequestReview } = useInAppReview()
const platform = baseStore.platform
const shouldDisplayAds = computed(() => {
  return baseStore.showAds
    && platform === 'web'
    && !('low' in route.query)
    && (!userStore?.user || Number(userStore.user.extra) !== 1)
})

// Imports pour les modals
const LazyNoteModal = defineAsyncComponent(() => import('~/components/modals/NoteModal.vue'))
const LazyRecipeBoxModal = defineAsyncComponent(() => import('~/components/modals/RecipeBoxModal.vue'))

// Validate route parameters
const recipeId = computed(() => {
  const id = route.params.id

  if (!id) {
    console.error('Recipe ID is missing from route params')
    return null
  }

  // Ensure id is a number
  const numericId = Number.parseInt(id as string, 10)
  if (Number.isNaN(numericId)) {
    console.error('Recipe ID is not a valid number:', id)
    return null
  }

  return numericId
})

/* const { data: recipe, pending, error } = await useFetch(`${config.public.apiUrl}/recipes/single`, {
  query: {
    id: recipeId.value,
  },
  key: `recipe-${recipeId.value}`,
  transform: (response) => {
    // Do anything you want here!
    return response
  },
  // Add error handling
  onResponseError({ response }) {
    console.error('Failed to fetch recipe:', response._data)
    toast.add({
      title: 'Erreur',
      description: 'Impossible de charger la recette. Veuillez réessayer.',
      color: 'error',
      icon: 'i-lucide-alert-circle',
    })
  },
}) */

const { data: recipe, pending, error } = await useFetch(`${config.public.apiUrl}/recipes/single`, {
  query: {
    id: recipeId,
  },
  watch: [recipeId],
  transform: (response) => {
    return response
  },
})

// Track recipe prints on client side when recipe is loaded
watch(
  recipe,
  (newRecipe) => {
    if (import.meta.client && newRecipe && newRecipe.id) {
      baseStore.trackRecipePrints([newRecipe], 'recipe_single').catch(() => {
        // Silent fail - tracking shouldn't block the UI
      })
    }
  },
  { immediate: true },
)

// Variables réactives pour les ingrédients sélectionnés
const selectedIngredients = ref([])

// Variables réactives pour les étapes d'instructions raturées
const completedSteps = ref(new Set())

// Fonctions pour les actions des dropdowns
async function addToShoppingList(items: any[]) {
  if (userStore.user) {
    if (items.length > 0) {
      try {
        const response = await $fetch(`${config.public.apiUrl}/grocery-list/add-ingredient-from-recipe`, {
          method: 'GET',
          params: {
            'recipe_id': recipe.value?.id,
            'items[]': items,
          },
          headers: {
            Authorization: `Bearer ${userStore.access_token}`,
          },
        }) as any

        if (response?.success === 1) {
          toast.add({
            id: 'shopping-list-added',
            title: 'Ingrédients ajoutés',
            description: items.length === 1 ? 'Ingrédient ajouté avec succès à votre liste d\'épicerie' : 'Ingrédients ajoutés avec succès à votre liste d\'épicerie',
            color: 'success',
            icon: 'i-lucide-check-circle',
          })
          void maybeRequestReview('groceryAdd')
        }
      }
      catch (err) {
        console.error('Erreur lors de l\'ajout à la liste d\'épicerie:', err)

        toast.add({
          id: 'shopping-list-add-error',
          title: 'Erreur',
          description: 'Impossible d\'ajouter les ingrédients à la liste d\'épicerie',
          color: 'error',
          icon: 'i-lucide-alert-circle',
        })
      }
    }
  }
  else {
    toast.add({
      id: 'shopping-list-login-required',
      title: 'Connexion requise',
      color: 'error',
      description: 'Vous devez être connecté à un compte pour utiliser la liste d\'épicerie.',
      actions: [{
        label: 'Se connecter',
        color: 'secondary',
        variant: 'outline',
        onClick: () => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
  }
}

async function addToShoppingListAlternate(items: any[]) {
  if (userStore.user) {
    if (items.length > 0) {
      try {
        const response = await $fetch(`${config.public.apiUrl}/grocery-list/add-ingredient-from-recipe-alt`, {
          method: 'GET',
          params: {
            'recipe_id': recipe.value?.id,
            'items[]': items,
          },
          headers: {
            Authorization: `Bearer ${userStore.access_token}`,
          },
        }) as any

        if (response?.success === 1) {
          toast.add({
            id: 'shopping-list-added',
            title: 'Ingrédients ajoutés',
            description: items.length === 1 ? 'Ingrédient ajouté avec succès' : 'Ingrédients ajoutés avec succès',
            color: 'success',
            icon: 'i-lucide-check-circle',
          })
          void maybeRequestReview('groceryAdd')
        }
      }
      catch (err) {
        console.error('Erreur lors de l\'ajout à la liste d\'épicerie:', err)

        toast.add({
          id: 'shopping-list-add-error',
          title: 'Erreur',
          description: 'Impossible d\'ajouter les ingrédients à la liste d\'épicerie',
          color: 'error',
          icon: 'i-lucide-alert-circle',
        })
      }
    }
  }
  else {
    toast.add({
      id: 'shopping-list-login-required',
      title: 'Connexion requise',
      color: 'error',
      description: 'Vous devez être connecté à un compte pour utiliser la liste d\'épicerie.',
      actions: [{
        label: 'Se connecter',
        color: 'secondary',
        variant: 'outline',
        onClick: () => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
  }
}

function openPlannerModal() {
  // Vérifier si l'utilisateur est connecté et a un abonnement Extra
  if (!userStore.user || !userStore.user.extra) {
    toast.add({
      title: 'Fonctionnalité Extra',
      description: 'Le planificateur de menus est disponible uniquement avec un abonnement Extra.',
      color: 'warning',
      icon: 'i-mdi-star',
      actions: [
        {
          label: 'Découvrir l\'Extra',
          color: 'primary',
          variant: 'solid',
          to: localePath({ name: 'ExtraPage' }),
        },
      ],
    })
    return
  }

  // Si l'utilisateur est connecté et a un abonnement Extra, ouvrir la modal
  modalStore.openPlannerCalendarModal(recipe.value)
}

function openNoteModal() {
  // Vérifier si l'utilisateur est connecté et a un abonnement Extra
  if (!userStore.user || !userStore.user.extra) {
    toast.add({
      title: 'Fonctionnalité Extra',
      description: 'L\'annotation de recettes est disponible uniquement avec un abonnement Extra.',
      color: 'warning',
      icon: 'i-mdi-star',
      actions: [
        {
          label: 'Découvrir l\'Extra',
          color: 'primary',
          variant: 'solid',
          to: localePath({ name: 'ExtraPage' }),
        },
      ],
    })
    return
  }

  // Si l'utilisateur est connecté et a un abonnement Extra, ouvrir la modal
  const modal = overlay.create(LazyNoteModal, {
    props: {
      recipeId: recipe.value?.id,
      recipeType: recipe.value?.external ? 'external' : 'recipe',
    },
  })
  modal.open()
}

function openRecipeBoxModal() {
  // Vérifier si l'utilisateur est connecté
  if (!userStore.user) {
    toast.add({
      title: 'Connexion requise',
      color: 'error',
      description: 'Vous devez être connecté pour classer une recette.',
      actions: [{
        label: 'Se connecter',
        color: 'secondary',
        variant: 'outline',
        onClick: () => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
    return
  }

  // Ouvrir la modal de boîte de recettes
  const modal = overlay.create(LazyRecipeBoxModal, {
    props: {
      recipeId: recipe.value?.id,
      recipeType: recipe.value?.external ? 'external' : 'recipe',
    },
  })
  modal.open()
}

// Fonction pour basculer l'état d'une étape (raturée/non raturée)
function toggleStep(index: number) {
  if (completedSteps.value.has(index)) {
    completedSteps.value.delete(index)
  }
  else {
    completedSteps.value.add(index)
  }
}

// Fonction pour gérer les clics sur les instructions
function handleInstructionsClick(event: Event) {
  const target = event.target as HTMLElement
  if (target.tagName === 'LI') {
    const li = target as HTMLLIElement
    const index = Array.from(li.parentElement?.children || []).indexOf(li)
    toggleStep(index)
  }
}

// Fonction pour ouvrir la recette dans le navigateur Capacitor
async function appBrowserOpen(url: string) {
  try {
    await Browser.open({ url, toolbarColor: '#F37A4E' })
  }
  catch (error) {
    console.error('Browser opening error: ', error)
  }
}

useHead({
  title: computed(() => recipe.value ? `${recipe.value.title} - Glouton` : 'Recette - Glouton'),
  meta: [
    { name: 'description', content: computed(() => recipe.value?.description || 'Découvrez cette délicieuse recette sur Glouton') },
    { property: 'og:title', content: computed(() => recipe.value ? `${recipe.value.title} - Glouton` : 'Recette - Glouton') },
    { property: 'og:description', content: computed(() => recipe.value?.description || 'Découvrez cette délicieuse recette sur Glouton') },
    { property: 'og:image', content: computed(() => recipe.value?.thumbnailUrl ? `${config.public.backendCdnUrl}${recipe.value.thumbnailUrl}` : 'https://glouton.b-cdn.net/site/images/social.jpg') },
  ],
})

// Watcher pour appliquer les classes aux éléments li quand les étapes sont complétées
watch(completedSteps, () => {
  nextTick(() => {
    const instructionContainer = document.querySelector('.instructions')
    if (instructionContainer) {
      const liElements = instructionContainer.querySelectorAll('li')
      liElements.forEach((li, index) => {
        if (completedSteps.value.has(index)) {
          li.classList.add('completed')
        }
        else {
          li.classList.remove('completed')
        }
      })
    }
  })
}, { deep: true })
</script>

<script lang="ts">
export default {
  name: 'RecipePage',
}
</script>

<template>
  <div id="RecipePage" class="pb-18">
    <!-- Loading state -->
    <div v-if="pending" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <UIcon name="i-mdi-loading" class="animate-spin text-4xl text-poivre-400 mb-4" />
        <p class="text-poivre-500">
          Chargement de la recette...
        </p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error || !recipe" class="flex items-center justify-center min-h-screen">
      <div class="text-center max-w-md mx-auto px-4">
        <UIcon name="i-mdi-alert-circle" class="text-4xl text-red-400 mb-4" />
        <h1 class="text-2xl font-bold text-poivre-800 mb-2">
          Erreur de chargement
        </h1>
        <p class="text-poivre-600 mb-6">
          Impossible de charger cette recette. Elle pourrait ne plus être disponible.
        </p>
        <UButton
          :to="localePath({ name: 'IndexPage' })"
          color="primary"
          icon="i-mdi-home"
          label="Retour à l'accueil"
        />
      </div>
    </div>

    <!-- Recipe content -->
    <div v-else id="RecipeBoxPage-anchor" class="max-w-7xl mx-auto md:px-4">
      <div class="bg-sel">
        <div class="pb-4 bg-white shadow md:rounded-b-lg">
          <div class="lg:mx-auto lg:px-8 sm:grid sm:grid-cols-3 lg:gap-8 sm:flex sm:items-center">
            <div class="relative sm:py-5">
              <div class="px-4 pt-6 sm:max-w-3xl sm:pl-7 lg:pl-0 sm:p-0">
                <div class="overflow-hidden shadow-lg aspect-w-36 aspect-h-25 rounded-lg sm:aspect-w-36 sm:aspect-h-25">
                  <img :src="`${$config.public.backendCdnUrl}${recipe.thumbnailUrl}`" alt="" class="object-cover w-full h-full rounded-md">
                </div>
              </div>
            </div>
            <div class="mt-6 sm:m-0 sm:col-span-2 sm:pl-2 xl:pl-4 sm:py-4 lg:py-0">
              <div class="max-w-full px-4 mx-auto sm:max-w-2xl sm:px-6 lg:px-0 lg:py-10 lg:max-w-none">
                <h1 class="text-3xl font-bold text-poivre xl:text-4xl text-center sm:text-left font-title tracking-wide">
                  {{ recipe.title }}
                </h1>
                <h4 class="text-poivre text-center sm:text-left">
                  Une recette proposée par {{ recipe.website_name }}
                </h4>
                <div v-if="recipe.type === 1" class="text-center sm:text-left mt-2">
                  <UButton v-if="platform === 'web'" as="a" :href="recipe.recipeUrl" class="open-link" target="_blank" icon="i-mdi-external-link">
                    Ouvrir la recette
                  </UButton>
                  <UButton v-else as="a" icon="i-mdi-external-link" @click="appBrowserOpen(recipe.recipeUrl)">
                    Ouvrir la recette
                  </UButton>
                </div>
                <div class="flex justify-center sm:block">
                  <div class="w-60 border-1 rounded border-poivre-100 p-2 mt-4">
                    <Share :url="`${$config.public.appUrl}${$route.path}`" :title="recipe.title" :description="recipe.title" :media="`${$config.public.backendCdnUrl}${recipe.thumbnailUrl}`" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 md:gap-3 mx-auto py-6 px-4 md:px-0">
          <UButton icon="i-mdi-calendar-plus" size="lg" color="secondary" label="Planifier" @click="openPlannerModal" />
          <UButton icon="i-mdi-note-plus-outline" color="secondary" size="lg" label="Annoter" @click="openNoteModal" />
          <UButton icon="i-mdi-archive-star-outline" color="secondary" size="lg" label="Classer" @click="openRecipeBoxModal" />
        </div>
        <div class="mx-auto px-4 md:px-0">
          <div class="flex flex-wrap mb-8  relative">
            <!-- Left column -->
            <div class="w-full mb-4 md:w-1/3 xl:w-1/4 space-y-6">
              <DisplayAd
                v-if="shouldDisplayAds"
                dfp-ad-unit-path="glouton/recettes"
                :sizes="[300, 250]"
                custom-class="mb-6 block"
              />

              <!-- Indice d'économie -->
              <div class="overflow-hidden bg-white rounded-lg shadow">
                <div class="px-4 py-3 bg-white border-b border-poivre-200 sm:px-4">
                  <div class="flex items-center justify-between ">
                    <h3 class="text-xl font-medium leading-6 text-poivre-800 font-title">
                      Potentiel d'économies
                    </h3>

                    <UButton icon="i-mdi-information" color="primary" variant="ghost" size="xs" @click="modalStore.openScoreHelpModal" />
                  </div>
                </div>

                <div>
                  <div v-if="userStore.user && recipe?.id">
                    <RecipeScoreBlock :recipe-id="recipe.id" />
                  </div>
                  <div v-else class="p-5">
                    <p class="text-sm">
                      Les membres peuvent voir l'indice d'économie et les rabais disponibles pour cette recette.
                    </p>
                    <p class="mt-2 text-sm">
                      <a href="#" class="underline font-medium text-venice inline-flex" @click.prevent="modalStore.openLoginModal()">Connectez-vous</a> ou <ULink class="underline font-medium text-venice inline-flex" :to="localePath({ name: 'RegisterPage' })">
                        créez un compte
                      </ULink> maintenant, c'est gratuit!
                    </p>
                  </div>
                </div>
              </div>

              <!-- Restrictions alimentaires -->
              <div class="overflow-hidden bg-white rounded-lg shadow ">
                <div class="px-4 py-4 bg-white border-b border-poivre-200">
                  <div class="flex flex-wrap items-center justify-between -mt-2 -ml-4 sm:flex-nowrap">
                    <div class="mt-2 ml-4">
                      <h3 class="text-xl font-medium leading-6 text-poivre-800 font-title">
                        Restrictions alimentaires
                      </h3>
                    </div>
                  </div>
                </div>

                <div class="divide-y divide-poivre-200">
                  <div v-if="recipe.restrictions" class="flex flex-wrap gap-2 p-4">
                    <span v-for="restriction in recipe.restrictions" :key="restriction" class="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-sorbet text-sauge">
                      {{ restriction }}
                    </span>
                    <span v-if="recipe.restrictions.length === 0" class="text-sm">Aucune restriction alimentaire pour l'instant.</span>
                  </div>
                </div>
              </div>

              <!-- Categories / thematiques -->
              <div class="overflow-hidden bg-white rounded-lg shadow">
                <div class="px-4 py-4 bg-white border-b border-poivre-200">
                  <div class="flex flex-wrap items-center justify-between -mt-2 -ml-4 sm:flex-nowrap">
                    <div class="mt-2 ml-4">
                      <h3 class="text-xl font-medium leading-6 text-poivre-800 font-title">
                        Catégories
                      </h3>
                    </div>
                  </div>
                </div>

                <div class="divide-y divide-poivre-200">
                  <div v-if="recipe.categories" class="flex flex-wrap gap-2 p-4">
                    <span v-for="category in recipe.categories" :key="category" class="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-saumon-500 text-sorbet-500">
                      {{ category }}
                    </span>
                    <span v-if="recipe.categories.length === 0" class="text-sm">Aucune catégorie pour l'instant.</span>
                  </div>
                </div>
              </div>

              <!-- Native Ad -->
              <ClientOnly>
                <AdManagerNativeAd />
              </ClientOnly>
            </div>

            <!-- Right column -->
            <div class="w-full md:w-2/3 xl:w-3/4 sm:mt-0 md:px-3 md:pl-6 space-y-6">
              <!-- Ingrédient/instructions type > 1 -->
              <div v-if="recipe.type > 1" class="mb-10">
                <div>
                  <div class="border-b border-poivre-100 mb-5">
                    <div class="flex items-center justify-between pb-5 pb-2">
                      <h3 class="text-2xl font-bold text-poivre-800 font-title">
                        Ingrédients
                      </h3>
                      <ClientOnly>
                        <UDropdownMenu
                          :content="{
                            align: 'end',
                            side: 'bottom',
                            sideOffset: 1,
                          }"
                          :items="[
                            {
                              label: 'Ajouter tous les ingrédients à la liste d\'épicerie',
                              icon: 'i-mdi-playlist-star',
                              click: () => addToShoppingListAlternate(recipe.ingredients_text),
                            },
                            {
                              label: `Ajouter les ingrédients sélectionnés à la liste d'épicerie (${selectedIngredients.length})`,
                              icon: 'i-mdi-playlist-plus',
                              click: () => addToShoppingListAlternate(selectedIngredients),
                            },

                          ]"
                        >
                          <UButton
                            variant="outline"
                            size="sm"
                            icon="i-mdi-menu-down"
                          >
                            Actions
                          </UButton>
                        </UDropdownMenu>
                      </ClientOnly>
                    </div>
                  </div>
                  <div>
                    <div v-if="recipe.ingredients_text" class="grid grid-cols-1 gap-3 mt-3">
                      <div v-for="(ingredient, key) in recipe.ingredients_text" :key="key">
                        <div v-if="ingredient.type === 'ingredient'" class="flex col-span-1 p-2 text-base bg-white border-poivre-200 rounded-md shadow-sm justify-space-between">
                          <div class="flex items-center mr-2">
                            <UCheckbox
                              :model-value="selectedIngredients.includes(ingredient)"
                              :value="ingredient"
                              @update:model-value="(checked) => {
                                if (checked) {
                                  selectedIngredients.push(ingredient)
                                }
                                else {
                                  const index = selectedIngredients.indexOf(ingredient)
                                  if (index > -1) {
                                    selectedIngredients.splice(index, 1)
                                  }
                                }
                              }"
                            />
                          </div>
                          <div class="flex items-center w-full">
                            <label :for="key" class="text-poivre-800"><span v-if="ingredient.quantity">{{ ingredient.quantity }} </span><span v-if="ingredient.quantity" class="text-tomate-400 font-bold mx-2">&middot;</span><span class="font-medium">{{ ingredient.name }} </span><span v-if="ingredient.description" class="text-tomate-400 font-bold mx-2">&middot;</span><span v-if="ingredient.description"> {{ ingredient.description }}</span></label>
                          </div>
                          <div class="flex items-center">
                            <ClientOnly>
                              <UDropdownMenu
                                :content="{
                                  align: 'end',
                                  side: 'bottom',
                                  sideOffset: 1,
                                }"
                                :items="[
                                  {
                                    label: 'Ajouter à la liste d\'épicerie',
                                    icon: 'i-mdi-playlist-plus',
                                    onSelect: () => addToShoppingListAlternate([ingredient]),
                                  },
                                ]"
                              >
                                <UButton
                                  variant="ghost"
                                  size="xs"
                                  icon="i-mdi-menu-down"
                                  class="text-poivre-400 hover:text-poivre-500"
                                />
                              </UDropdownMenu>
                            </ClientOnly>
                          </div>
                        </div>
                        <div v-else>
                          <h3 class="uppercase font-medium">
                            {{ ingredient.name }}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-poivre-500">
                      Aucune suggestion pour l'instant.
                    </div>
                  </div>
                </div>

                <div v-if="recipe.notes_text">
                  <div class="mb-5 border-b border-poivre-100 mt-10">
                    <h3 class="text-2xl font-bold text-poivre-800 pb-2 font-title">
                      Notes
                    </h3>
                  </div>
                  <div class="p-5 text-base bg-white border-poivre-200 rounded-md shadow-sm">
                    <div class="notes">
                      <div v-html="recipe.notes_text" />
                    </div>
                  </div>
                </div>

                <div>
                  <div class="mb-5 border-b border-poivre-100 mt-10">
                    <h3 class="text-2xl font-bold text-poivre-800 pb-2 font-title">
                      Instructions
                    </h3>
                  </div>
                  <div class="p-5 text-base bg-white border-poivre-200 rounded-md shadow-sm">
                    <div class="instructions">
                      <div @click="handleInstructionsClick" v-html="recipe.instructions_text" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Ingrédients en rabais -->
              <div>
                <div class="mb-5 border-b border-poivre-100">
                  <h3 class="text-2xl font-bold text-poivre-800 pb-2 font-title">
                    Ingrédients en rabais
                  </h3>
                </div>
                <div v-if="userStore.user && recipe?.id">
                  <div class="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                    <UIcon name="i-mdi-information" class="text-bleuet-600 text-lg" />

                    <div class="text-bleuet-800 text-sm leading-relaxed">
                      Résultats pour les <span class="font-semibold text-blue-900">{{ userStore.user.stores.length }}</span> commerces liés à votre profil.
                      <ULink class="font-medium text-bleuet-700 hover:text-bleuet-800 underline ml-1" :to="localePath({ name: 'UserProfilePage' })">
                        Modifier
                      </ULink>
                    </div>
                  </div>
                  <PromoCards :recipe-id="recipe.id" />
                </div>
                <div v-else class="p-5 text-base bg-white border-poivre-200 rounded-md shadow-sm">
                  <p>Voyez quels ingrédients de cette recette sont en rabais dans vos supermarchés avec un compte Glouton.</p>
                  <p class="mt-2">
                    <a href="#" class="underline font-medium text-venice inline-flex" @click.prevent="modalStore.openLoginModal()">Connectez-vous</a> ou <ULink class="underline font-medium text-venice inline-flex" :to="localePath({ name: 'RegisterPage' })">
                      créez un compte
                    </ULink> maintenant, c'est gratuit!
                  </p>
                </div>
              </div>

              <DisplayAd
                v-if="shouldDisplayAds"
                dfp-ad-unit-path="glouton/recettes"
                :sizes="[[300, 250]]"
                :size-mapping="[[[0, 0], [[300, 250]]]]"
                custom-class="my-6"
              />

              <!-- Ingrédients type 1 -->
              <div v-if="recipe.type === 1">
                <div class="border-b border-poivre-100 mb-5">
                  <div class="flex items-center justify-between pb-2">
                    <h3 class="text-2xl font-bold text-poivre-800 font-title">
                      Ingrédients
                    </h3>

                    <ClientOnly>
                      <UDropdownMenu
                        :content="{
                          align: 'end',
                          side: 'bottom',
                          sideOffset: 1,
                        }"
                        :items="[
                          {
                            label: 'Ajouter tous les ingrédients à la liste d\'épicerie',
                            icon: 'i-mdi-playlist-star',
                            onSelect: () => addToShoppingList(recipe.ingredients),
                          },
                          {
                            label: `Ajouter les ingrédients sélectionnés à la liste d'épicerie (${selectedIngredients.length})`,
                            icon: 'i-mdi-playlist-plus',
                            onSelect: () => addToShoppingList(selectedIngredients),
                          },
                        ]"
                      >
                        <UButton
                          variant="outline"
                          size="sm"
                          icon="i-mdi-menu-down"
                        >
                          Actions
                        </UButton>
                      </UDropdownMenu>
                    </ClientOnly>
                  </div>
                </div>
                <div>
                  <div v-if="recipe.ingredients && recipe.ingredients.length > 0" class="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-1 lg:grid-cols-2">
                    <div v-for="ingredient in recipe.ingredients" :key="ingredient.id" class="flex col-span-1 p-2 text-base bg-white border-poivre-200 rounded-md shadow-sm justify-space-between">
                      <div class="flex items-center mr-2">
                        <UCheckbox
                          :model-value="selectedIngredients.includes(ingredient)"
                          :value="ingredient"
                          @update:model-value="(checked) => {
                            if (checked) {
                              selectedIngredients.push(ingredient)
                            }
                            else {
                              const index = selectedIngredients.indexOf(ingredient)
                              if (index > -1) {
                                selectedIngredients.splice(index, 1)
                              }
                            }
                          }"
                        />
                      </div>
                      <div class="flex items-center w-full">
                        <label :for="ingredient.id" class="flex items-center w-full text-poivre-800"><UIcon name="i-mdi-circle-small" class="hidden text-tomate" /> <span>{{ ingredient.name }}</span></label>
                      </div>
                      <div class="flex items-center">
                        <ClientOnly>
                          <UDropdownMenu
                            :content="{
                              align: 'end',
                              side: 'bottom',
                              sideOffset: 1,
                            }"
                            :items="[
                              {
                                label: 'Ajouter à la liste d\'épicerie',
                                icon: 'i-mdi-playlist-plus',
                                onSelect: () => addToShoppingList([ingredient]),
                              },
                            ]"
                          >
                            <UButton
                              variant="ghost"
                              size="xs"
                              icon="i-mdi-menu-down"
                              class="text-poivre-400 hover:text-poivre-500"
                            />
                          </UDropdownMenu>
                        </ClientOnly>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-poivre-500">
                    Aucun élément associé.
                  </div>
                </div>
              </div>

              <DisplayAd
                v-if="shouldDisplayAds"
                dfp-ad-unit-path="glouton/recettes"
                :sizes="[[300, 250], [728, 90], [970, 250]]"
                :size-mapping="[[[0, 0], [[300, 250]]], [[1024, 0], [[970, 250], [728, 90]]]]"
                custom-class="my-6"
              />

              <!-- Similar -->
              <div>
                <div class="mb-5 border-b border-poivre-100">
                  <h3 class="text-2xl font-bold text-poivre-800 pb-2 font-title">
                    Vous aimerez peut-être...
                  </h3>
                </div>
                <div>
                  <div v-if="recipe.similar && recipe.similar.length > 0" class="grid grid-cols-1 gap-4 mt-3 lg:grid-cols-2">
                    <ULink v-for="recipe in recipe.similar" :key="recipe.id" as="a" :to="localePath({ name: 'RecipePage', params: { id: recipe.id, slug: recipe.slug } })" class="flex col-span-1 bg-white border-poivre-200 rounded-md shadow-sm">
                      <div class="w-aspect-36 w-aspect-25 flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md">
                        <img :src="`${config.public.backendCdnUrl}${recipe.thumbnailUrl}`" class="w-full h-full rounded-l-md" loading="lazy">
                      </div>
                      <div class="flex items-center justify-between flex-1 rounded-r-md">
                        <div class="flex-1 px-4 py-2 text-sm">
                          <span class="font-medium text-poivre-800 hover:text-poivre-600 line-clamp-2">{{ recipe.title }}</span>
                          <p class="text-poivre-500 text-xs">
                            {{ recipe.author }}
                          </p>
                        </div>
                      </div>
                    </ULink>
                  </div>
                  <div v-else class="text-poivre-500">
                    Aucune suggestion pour l'instant.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.instructions :deep(ol) {
  line-height: 1.3em;
  counter-reset: ing-counter;
  list-style-position: outside;
  margin-top: 0px;
  margin-bottom: 10px;
  margin-left: 35px;
  padding-left: 0;
  counter-reset: item;
}

.instructions :deep(ul) {
  line-height: 1.3em;
  counter-reset: ing-counter;
  list-style-position: outside;
  margin-top: 0px;
  margin-bottom: 10px;
  margin-left: 35px;
  padding-left: 0;
  counter-reset: item;
}

.instructions :deep(li) {
  line-height: 1.3em;
  margin: 0 0 10px 0;
  padding: 0 0 0 0;
  list-style-type: none;
  counter-increment: item;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.instructions :deep(li:hover) {
  background-color: rgba(59, 130, 246, 0.05);
  border-radius: 4px;
  padding: 2px 4px;
  margin: 0 0 8px 0;
}

.instructions :deep(li.completed) {
  text-decoration: line-through;
  color: #6b7280;
  opacity: 0.7;
}

.instructions :deep(li:before) {
  display: inline-block;
  text-indent: -1.8em;
  font-weight: 500;
  text-align: left;
  color: color(darkblue);
  content: counter(item) '.';
}

.instructions :deep(li:last-child) {
  margin: 0 !important;
}

.instructions :deep(p) {
  margin-bottom: 10px;
}

.instructions :deep(h1),
.instructions :deep(h2),
.instructions :deep(h3),
.instructions :deep(h4) {
  font-weight: bold;
  margin-bottom: 10px;
}
</style>
